import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow-right.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import TextField from 'components/TextField/TextField';
import {
  ORDER_KEY, ORDER_TYPE, SEARCH, SORT, VALUE_CHAIN_MAPS,
} from 'constants/formFields';
import {
  DEFAULT_ITEM, DEFAULT_LIMIT, DEFAULT_VC_COUNT, TASK_TYPE_ACTION,
} from 'constants/general';
import { BUTTON_VARIANT, ORDERS, VALUE_CHAIN_STATUS } from 'constants/interfaces';
import { DEFAULT_VC_ITEM } from 'constants/materialAssessment';
import useDebouncedCallback from 'hooks/useDebouncedCallback';
import useErrorMessage from 'hooks/useErrorMessage';
import i18n from 'i18n';
import { useGetValueChainMapListQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';
import { getTextDate } from 'utils/formattedData';

import useInitiateVCForm from '../hooks/useInitiateVCForm';

import type { ISearchInputHandler } from 'components/TextField/interfaces/ITextField';
import type IValueChainMap from 'store/slices/valueChainMap/interfaces/IValueChainMap';
import type IInitialVCmodal from '../interfaces/IInitialVCmodal';
import type ISearchVCParams from '../interfaces/ISearchVCParams';
import type IValueChainMapItem from '../interfaces/IValueChainMapItem';

import styles from '../sass/InitialVCmodal.module.scss';

function InitialVCmodal({
  open, onClose, setValueForm, typeModal, currentSelectedVC, onCancel, accessEdit,
}: Readonly<IInitialVCmodal>) {
  const { t } = useTranslation();
  const [params, setParams] = useState<ISearchVCParams>({});
  const [orderBy, setOrderBy] = useState('');
  const [selectedVC, setSelectedVC] = useState<IValueChainMapItem[]>(currentSelectedVC || []);
  const [temporarySelectedVC, setTemporarySelectedVC] = useState<IValueChainMapItem[]>(currentSelectedVC || []);
  const [allItems, setAllItems] = useState<IValueChainMap[]>([]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [shouldClearData, setShouldClearData] = useState(false);
  const hyphen = t('common.hyphen');
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
  } = useInitiateVCForm();

  const {
    data,
    isFetching,
    refetch,
    error: fetchError,
  } = useGetValueChainMapListQuery({
    limit: DEFAULT_LIMIT,
    offset: currentOffset,
    status: [VALUE_CHAIN_STATUS.approved],
    ...(params.search && params.search !== '' && { search: params.search }),
    ...(orderBy && { orderBy }),
  });
  const errorMessage = useErrorMessage(fetchError);

  useEffect(() => {
    if (errorMessage && errorMessage !== '') {
      toast.error(i18n.t('notifications.errorHandlerTextMessage', { message: errorMessage }));
    }
  }, [errorMessage]);

  const meta = data?.data?.meta;

  const debouncedSearch = useDebouncedCallback(() => {
    const { search } = getValues();

    setParams((prev) => ({ ...prev, search }));
    setCurrentOffset(0);
    setAllItems([]);
    setHasMoreData(true);
    setShouldClearData(true);
  });

  useEffect(() => {
    if (data?.data?.items) {
      if (currentOffset === 0) {
        setAllItems(data.data.items);
      } else {
        setAllItems((prev) => [...prev, ...data.data.items]);
      }

      if (meta?.hasNext !== undefined) {
        setHasMoreData(meta.hasNext);
      } else {
        setHasMoreData(data.data.items.length === DEFAULT_LIMIT);
      }
    }
  }, [data, currentOffset, meta]);

  useEffect(() => {
    if (shouldClearData) {
      setCurrentOffset(0);
      setHasMoreData(true);
      setShouldClearData(false);
    }
  }, [orderBy, params.search, shouldClearData]);

  const loadMoreData = () => {
    if (!isFetching && hasMoreData) {
      setCurrentOffset((prev) => prev + DEFAULT_LIMIT);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - scrollTop <= clientHeight + 100) {
      loadMoreData();
    }
  };

  const onSearchChange = ({ search, onChange }: ISearchInputHandler) => {
    onChange(search);
    debouncedSearch();
    refetch();
  };

  const onVCManagementSubmit = handleSubmit(() => {});

  useEffect(() => {
    if (currentSelectedVC) {
      setSelectedVC(currentSelectedVC);
      setTemporarySelectedVC(currentSelectedVC);

      if (typeModal === TASK_TYPE_ACTION.EDIT) {
        const formattedSelectedVC = currentSelectedVC.map((item) => ({
          ...item,
          createdBy: item.createdBy || DEFAULT_ITEM,
          updatedBy: item.updatedBy || DEFAULT_ITEM,
        }));
        setValueForm(VALUE_CHAIN_MAPS, formattedSelectedVC);
      }
    }
  }, [currentSelectedVC, setValueForm, typeModal]);

  const handleUserSelect = (selectedVCItem: IValueChainMap) => {
    const exists = temporarySelectedVC.some((u) => u.id === selectedVCItem.id);
    const isInitiallySelectedItem = currentSelectedVC?.some((u) => u.id === selectedVCItem.id);

    if (exists && isInitiallySelectedItem && accessEdit === false) {
      return;
    }

    if (!exists && temporarySelectedVC.length >= DEFAULT_VC_COUNT) {
      return;
    }

    const item = {
      id: selectedVCItem.id,
      name: selectedVCItem.name,
      createdBy: selectedVCItem.createdBy || DEFAULT_ITEM,
      updatedBy: selectedVCItem.updatedBy || DEFAULT_ITEM,
      status: selectedVCItem.status,
      createdAt: selectedVCItem.createdAt || '',
      updatedAt: selectedVCItem.updatedAt || '',
    };
    let updated: IValueChainMapItem[];
    if (exists) {
      updated = temporarySelectedVC.filter((u) => u.id !== selectedVCItem.id);
    } else {
      updated = [...temporarySelectedVC, item];
    }
    setTemporarySelectedVC(updated);
  };

  const isUserSelected = (id: number) => temporarySelectedVC.some((u) => u.id === id);

  const isInitiallySelected = (id: number) => currentSelectedVC?.some((u) => u.id === id) || false;

  const onCancelVCSelect = () => {
    setTemporarySelectedVC(selectedVC);

    if (currentSelectedVC && currentSelectedVC.length === 0) {
      setSelectedVC([]);
      setValueForm(VALUE_CHAIN_MAPS, []);
    }
    onCancel();
  };

  const handleAddSelectedVC = () => {
    setSelectedVC(temporarySelectedVC);
    const formattedUpdated = temporarySelectedVC.map((vcItem) => ({
      ...vcItem,
      createdBy: vcItem.createdBy || DEFAULT_VC_ITEM,
      updatedBy: vcItem.updatedBy || DEFAULT_VC_ITEM,
    }));
    setValueForm(VALUE_CHAIN_MAPS, formattedUpdated, { shouldDirty: true });
    onClose();
  };

  const unselectAll = () => {
    setTemporarySelectedVC(currentSelectedVC || []);
  };

  return (
    <Modal
      open={open}
      isButtonsVisible={false}
      className={styles.modal}
      title={t('materialityAssessment.selectValueChain')}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.search}>
            <Controller
              control={control}
              name={SEARCH}
              render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                <TextField
                  name={name}
                  value={value}
                  placeholder={t('materialityAssessment.fields.search')}
                  error={error?.message}
                  icon={<SearchIcon />}
                  onChange={(event) => {
                    onChange(event.target.value);
                    onSearchChange({ search: event.target.value, onChange });
                    onVCManagementSubmit();
                  }}
                  required
                />
              )}
            />
          </div>
          <div className={styles.selectedCounter}>
            {temporarySelectedVC.length >= DEFAULT_VC_COUNT
              ? `Selected ${temporarySelectedVC.length}/${DEFAULT_VC_COUNT} (Maximum reached)`
              : `Selected ${temporarySelectedVC.length}/${DEFAULT_VC_COUNT}`}
          </div>
        </div>
        <div className={styles.usersTable} onScroll={handleScroll}>
          {isFetching && currentOffset === 0 ? <Loader isPageLoading={isFetching} /> : null}
          {allItems && allItems.length > 0 ? (
            <>
              <table>
                <thead>
                  <tr className={styles.tableHeader}>
                    <th className={styles.checkboxColumn} aria-label={t('common.select')}>
                      <Checkbox
                        name="vs-all"
                        className={clsx(styles.checkbox, [temporarySelectedVC.length === 0 && styles.disabled])}
                        checked={temporarySelectedVC.length > 0}
                        value={String(temporarySelectedVC.length > 0)}
                        onChange={unselectAll}
                        label={false}
                        aria-label={t('common.select')}
                        disabled={temporarySelectedVC.length === 0}
                        isLineMark
                      />
                    </th>
                    <th className={styles.nameColumn}>
                      <div className={styles.sortableHeader}>
                        <span>{t('materialityAssessment.valueChainName')}</span>
                        <div className={styles.sortArrows}>
                          <button
                            type="button"
                            className={`${styles.sortButton} ${orderBy === 'name' ? styles.active : ''}`}
                            onClick={() => {
                              setValue(SORT, { [ORDER_KEY]: 'name', [ORDER_TYPE]: ORDERS.asc });
                              setOrderBy('name');
                              setShouldClearData(true);
                            }}
                            tabIndex={0}
                            aria-label={t('taskManagement.sortNameAsc')}
                          >
                            <ArrowRightIcon className={styles.arrowUp} />
                          </button>
                          <button
                            type="button"
                            className={`${styles.sortButton} ${orderBy === '-name' ? styles.active : ''}`}
                            onClick={() => {
                              setValue(SORT, { [ORDER_KEY]: '-name', [ORDER_TYPE]: ORDERS.desc });
                              setOrderBy('-name');
                              setShouldClearData(true);
                            }}
                            tabIndex={0}
                            aria-label={t('taskManagement.sortNameDesc')}
                          >
                            <ArrowRightIcon className={styles.arrowDown} />
                          </button>
                        </div>
                      </div>
                    </th>
                    <th className={styles.roleColumn}>
                      {t('materialityAssessment.createdBy')}
                    </th>
                    <th className={styles.roleColumn}>
                      {t('materialityAssessment.updatedBy')}
                    </th>
                    <th className={styles.roleColumn}>
                      {t('materialityAssessment.status')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {allItems.map((vcItem: IValueChainMap) => {
                    const isDisabled = temporarySelectedVC.length >= DEFAULT_VC_COUNT && !isUserSelected(vcItem.id);
                    const isLocked = isInitiallySelected(vcItem.id) && accessEdit === false;
                    return (
                      <tr
                        key={vcItem.id}
                        className={`${styles.tableRow} ${isDisabled ? styles.disabled : ''}`}
                      >
                        <td className={styles.tableCellCheckbox}>
                          <Checkbox
                            name={`user-${vcItem.id}`}
                            checked={isUserSelected(vcItem.id)}
                            value={String(isUserSelected(vcItem.id))}
                            onChange={() => handleUserSelect(vcItem)}
                            label={false}
                            aria-label={`${vcItem.name}`}
                            disabled={isDisabled || isLocked}
                          />
                        </td>
                        <td className={styles.tableCellName}>
                          <div className={styles.name}>
                            {vcItem.name}
                          </div>
                        </td>
                        <td className={styles.tableCellRole}>
                          <div className={`${styles.roleCell} ${styles.updatedBy}`}>
                            <span>
                              {vcItem.createdAt
                                ? getTextDate(vcItem.createdAt)
                                : hyphen}
                            </span>
                            <span>
                              by
                              {' '}
                              {vcItem.createdBy
                                ? `${vcItem.createdBy.firstName} ${vcItem.createdBy.lastName}`
                                : hyphen}
                            </span>
                          </div>
                        </td>
                        <td className={styles.tableCellRole}>
                          <div className={`${styles.roleCell} ${styles.updatedBy}`}>
                            <span>
                              {vcItem.updatedAt
                                ? getTextDate(vcItem.updatedAt)
                                : hyphen}
                            </span>
                            <span>
                              by
                              {' '}
                              {vcItem.updatedBy
                                ? `${vcItem.updatedBy.firstName} ${vcItem.updatedBy.lastName}`
                                : hyphen}
                            </span>
                          </div>
                        </td>
                        <td className={styles.tableCellRole}>
                          <div className={styles.roleCell}>
                            <span
                              className={`${styles.statusBadge} ${
                                styles[vcItem.status?.toLowerCase() || 'current']
                              }`}
                            >
                              {vcItem.status || hyphen}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(isFetching && currentOffset > 0) ? (
                <div className={styles.loader}>
                  <SmallLoader />
                </div>
              ) : null}
            </>
          ) : null}
          {!isFetching && allItems.length === 0 && (
            <div className={styles.noFound}>{t('materialityAssessment.noVCFound')}</div>
          )}
        </div>
      </div>
      <div className={styles.buttonWrapper}>
        <Button
          title={t('common.cancel')}
          variant={BUTTON_VARIANT.cancel}
          className={styles.button}
          onClick={onCancelVCSelect}
        />
        <Button
          title={t('common.add')}
          onClick={handleAddSelectedVC}
          className={styles.button}
        />
      </div>
    </Modal>
  );
}

export default InitialVCmodal;
